/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * Addon Form Modal Component
 * Modal dialog for creating and editing addon library items
 */

import { Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Addon, MenuItemStatus } from '../../../types/menu';
import { DropdownSelect } from '../../common/DropdownSelect';
import { Input } from '../../common/Input';

interface AddonFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    default_price: number;
    status: MenuItemStatus;
  }) => void | Promise<void>;
  addon?: Addon | null;
  isLoading?: boolean;
}

export const AddonFormModal: React.FC<AddonFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  addon,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');
  const [status, setStatus] = useState<MenuItemStatus>('active');
  const [errors, setErrors] = useState<{
    name?: string;
    defaultPrice?: string;
  }>({});

  const isEditMode = !!addon;

  // Reset form when modal opens/closes or addon changes
  useEffect(() => {
    if (isOpen) {
      if (addon) {
        setName(addon.name);
        setDefaultPrice(addon.default_price.toString());
        setStatus(addon.status);
      } else {
        setName('');
        setDefaultPrice('');
        setStatus('active');
      }
      setErrors({});
    }
  }, [isOpen, addon]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; defaultPrice?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Addon name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!defaultPrice.trim()) {
      newErrors.defaultPrice = 'Price is required';
    } else if (
      Number.isNaN(parseFloat(defaultPrice)) ||
      parseFloat(defaultPrice) < 0
    ) {
      newErrors.defaultPrice = 'Price must be a valid non-negative number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    await onSubmit({
      name: name.trim(),
      default_price: parseFloat(defaultPrice),
      status,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Edit Addon' : 'Create Addon'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Addon Name"
            value={name}
            onChange={(e) => setName(e.toString())}
            placeholder="e.g. Extra Cheese"
            error={errors.name}
            required
            helperText="Internal name for the addon"
          />

          <Input
            label="Default Price"
            type="number"
            value={defaultPrice}
            onChange={(e) => setDefaultPrice(e.toString())}
            placeholder="0.00"
            error={errors.defaultPrice}
            required
          />

          <DropdownSelect
            label="Status"
            value={status}
            onChange={(v) => setStatus(v as MenuItemStatus)}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'out_of_stock', label: 'Out of Stock' },
            ]}
          />

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-pos-primary text-white font-medium rounded-lg hover:bg-pos-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>{isEditMode ? 'Update Addon' : 'Create Addon'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
